"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  TITRATION_CURVE_STRINGS,
  TitrationCurvePanel,
} from "@/components/tools/chem-reactions/titration-curve-panel";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  analyteFromEndpoint,
  equivalenceVolume,
} from "@/lib/tool-kit/chem-reactions/titration";
import {
  fewestSigFigs,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const TITRATION_CALCULATOR_STRINGS = {
  ...TITRATION_CURVE_STRINGS,
  modeLabel: "What do you want to work out?",
  modeConcentration: "Unknown concentration from the endpoint",
  modeVolume: "Titrant volume at equivalence",
  modeCurve: "Titration curve",
  titrantConcentration: "Titrant concentration (mol/L)",
  titrantVolume: "Titrant volume at the endpoint (mL)",
  analyteVolume: "Volume of analyte titrated (mL)",
  analyteConcentration: "Analyte concentration (mol/L)",
  analyteCoefficient: "Analyte coefficient (a)",
  titrantCoefficient: "Titrant coefficient (b)",
  ratioHint:
    "From the balanced equation, a analyte + b titrant: 1 and 1 for HCl + NaOH.",
  ratioPresets: "Common ratios",
  presetMonoprotic: "1 : 1 acid + NaOH",
  presetDiprotic: "1 : 2 H₂SO₄ + NaOH",
  presetPermanganate: "5 : 1 Fe²⁺ + MnO₄⁻",
  molarMassLabel: "Molar mass of the analyte (g/mol, optional)",
  molarMassHint: "Adds the concentration in g/L.",
  results: "Results",
  analyteResult: "Analyte concentration",
  titrantMolesResult: "Moles of titrant",
  analyteMolesResult: "Moles of analyte",
  massConcentration: "Mass concentration",
  equivalenceResult: "Titrant volume at equivalence",
  enter: "Enter the values above to see the result.",
  badValue: "Every value must be a number greater than zero.",
  workingHeading: "The working",
  titrantMolesLine:
    "n(titrant) = {concentration} mol/L × {volume} mL × 10⁻³ L/mL = {moles} mol",
  analyteMolesLine: "n(analyte) = {moles} mol × {a} ÷ {b} = {analyteMoles} mol",
  concentrationLine:
    "c(analyte) = {analyteMoles} mol ÷ ({volume} mL × 10⁻³ L/mL) = {result} mol/L",
  massLine: "{result} mol/L × {molar} g/mol = {mass} g/L",
  volumeLine:
    "V(titrant) = {concentration} mol/L × {volume} mL × {b} ÷ ({a} × {titrant} mol/L) = {result} mL",
  sigNote:
    "Rounded to {count} significant figures, the fewest in the measurements you entered. The coefficients are exact.",
  copy: "Copy result",
  copied: "Copied!",
  copyConcentration: "Analyte concentration: {result} mol/L",
  copyVolume: "Titrant volume at equivalence: {result} mL",
} as const;

type Strings = typeof TITRATION_CALCULATOR_STRINGS;
type Mode = "concentration" | "volume" | "curve";

const DEFAULT_SIG = 4;
const GUARD = 1;
const ML_TO_L = 1e-3;
const RATIO_PRESETS = [
  { key: "presetMonoprotic", a: "1", b: "1" },
  { key: "presetDiprotic", a: "1", b: "2" },
  { key: "presetPermanganate", a: "5", b: "1" },
] as const;

function parsePositive(raw: string): number | null {
  const value = parseMeasuredNumber(raw);
  return value !== null && value > 0 ? value : null;
}

interface Ratio {
  a: string;
  b: string;
}

function RatioFields({
  ratio,
  onChange,
  strings,
}: {
  ratio: Ratio;
  onChange: (ratio: Ratio) => void;
  strings: Strings;
}) {
  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.ratioHint}
          id="tit-a"
          label={strings.analyteCoefficient}
          numeric
          onChange={(a) => onChange({ ...ratio, a })}
          value={ratio.a}
        />
        <TextField
          id="tit-b"
          label={strings.titrantCoefficient}
          numeric
          onChange={(b) => onChange({ ...ratio, b })}
          value={ratio.b}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.ratioPresets}</span>
        {RATIO_PRESETS.map((preset) => (
          <button
            className={BUTTON_SMALL}
            key={preset.key}
            onClick={() => onChange({ a: preset.a, b: preset.b })}
            type="button"
          >
            {strings[preset.key]}
          </button>
        ))}
      </div>
    </div>
  );
}

interface Endpoint {
  titrant: number;
  titrantVol: number;
  analyteVol: number;
  a: number;
  b: number;
}

function ConcentrationAnswer({
  endpoint,
  sig,
  molar,
  strings,
  fmt,
}: {
  endpoint: Endpoint;
  sig: number;
  molar: number | null;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const result = analyteFromEndpoint(
    {
      titrantConcentration: endpoint.titrant,
      titrantVolume: endpoint.titrantVol,
      analyteVolume: endpoint.analyteVol,
      analyteCoefficient: endpoint.a,
      titrantCoefficient: endpoint.b,
    },
    ML_TO_L
  );
  const guard = sig + GUARD;
  const concentration = formatSig(fmt, result.analyteConcentration, sig);
  const lines = [
    t(strings.titrantMolesLine, {
      concentration: formatSig(fmt, endpoint.titrant, guard),
      volume: formatSig(fmt, endpoint.titrantVol, guard),
      moles: formatSig(fmt, result.titrantMoles, guard),
    }),
    t(strings.analyteMolesLine, {
      moles: formatSig(fmt, result.titrantMoles, guard),
      a: fmt(endpoint.a),
      b: fmt(endpoint.b),
      analyteMoles: formatSig(fmt, result.analyteMoles, guard),
    }),
    t(strings.concentrationLine, {
      analyteMoles: formatSig(fmt, result.analyteMoles, guard),
      volume: formatSig(fmt, endpoint.analyteVol, guard),
      result: formatSig(fmt, result.analyteConcentration, guard),
    }),
  ];
  if (molar !== null) {
    lines.push(
      t(strings.massLine, {
        result: formatSig(fmt, result.analyteConcentration, guard),
        molar: fmt(molar),
        mass: formatSig(fmt, result.analyteConcentration * molar, sig),
      })
    );
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.analyteResult}
          value={`${concentration} mol/L`}
        />
        <StatCard
          label={strings.titrantMolesResult}
          value={`${formatSig(fmt, result.titrantMoles, sig)} mol`}
        />
        <StatCard
          label={strings.analyteMolesResult}
          value={`${formatSig(fmt, result.analyteMoles, sig)} mol`}
        />
        {molar === null ? null : (
          <StatCard
            label={strings.massConcentration}
            value={`${formatSig(fmt, result.analyteConcentration * molar, sig)} g/L`}
          />
        )}
      </StatGrid>
      <Notice>{t(strings.sigNote, { count: fmt(sig) })}</Notice>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <div className={WORKING_BOX}>
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyConcentration, { result: concentration })}
      />
    </>
  );
}

function ConcentrationMode({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [titrant, setTitrant] = useState("0.1000");
  const [titrantVol, setTitrantVol] = useState("24.35");
  const [analyteVol, setAnalyteVol] = useState("25.00");
  const [ratio, setRatio] = useState<Ratio>({ a: "1", b: "1" });
  const [molar, setMolar] = useState("");

  const parsed = {
    titrant: parsePositive(titrant),
    titrantVol: parsePositive(titrantVol),
    analyteVol: parsePositive(analyteVol),
    a: parsePositive(ratio.a),
    b: parsePositive(ratio.b),
  };
  const molarValue = molar.trim() === "" ? null : parsePositive(molar);
  const empty = [titrant, titrantVol, analyteVol].some(
    (raw) => raw.trim() === ""
  );
  const { titrant: c, titrantVol: tv, analyteVol: av, a, b } = parsed;

  let body = <Notice>{strings.enter}</Notice>;
  if (!empty) {
    body =
      c === null ||
      tv === null ||
      av === null ||
      a === null ||
      b === null ||
      (molar.trim() !== "" && molarValue === null) ? (
        <ErrorNotice>{strings.badValue}</ErrorNotice>
      ) : (
        <ConcentrationAnswer
          endpoint={{ titrant: c, titrantVol: tv, analyteVol: av, a, b }}
          fmt={fmt}
          molar={molarValue}
          sig={fewestSigFigs([titrant, titrantVol, analyteVol], DEFAULT_SIG)}
          strings={strings}
        />
      );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="tit-titrant"
          label={strings.titrantConcentration}
          numeric
          onChange={setTitrant}
          value={titrant}
        />
        <TextField
          id="tit-titrant-vol"
          label={strings.titrantVolume}
          numeric
          onChange={setTitrantVol}
          value={titrantVol}
        />
        <TextField
          id="tit-analyte-vol"
          label={strings.analyteVolume}
          numeric
          onChange={setAnalyteVol}
          value={analyteVol}
        />
      </div>
      <RatioFields onChange={setRatio} ratio={ratio} strings={strings} />
      <TextField
        hint={strings.molarMassHint}
        id="tit-molar"
        label={strings.molarMassLabel}
        numeric
        onChange={setMolar}
        value={molar}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function VolumeMode({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [analyte, setAnalyte] = useState("0.1000");
  const [analyteVol, setAnalyteVol] = useState("25.00");
  const [titrant, setTitrant] = useState("0.1000");
  const [ratio, setRatio] = useState<Ratio>({ a: "1", b: "1" });
  const ca = parsePositive(analyte);
  const va = parsePositive(analyteVol);
  const ct = parsePositive(titrant);
  const a = parsePositive(ratio.a);
  const b = parsePositive(ratio.b);
  let body = <ErrorNotice>{strings.badValue}</ErrorNotice>;
  if (ca !== null && va !== null && ct !== null && a !== null && b !== null) {
    const volume = equivalenceVolume(ca, va, ct, a, b);
    const sig = fewestSigFigs([analyte, analyteVol, titrant], DEFAULT_SIG);
    const text = formatSig(fmt, volume, sig);
    body = (
      <>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.equivalenceResult}
            value={`${text} mL`}
          />
        </StatGrid>
        <Notice>{t(strings.sigNote, { count: fmt(sig) })}</Notice>
        <div className={WORKING_BOX}>
          <p>
            {t(strings.volumeLine, {
              concentration: formatSig(fmt, ca, sig + GUARD),
              volume: formatSig(fmt, va, sig + GUARD),
              a: fmt(a),
              b: fmt(b),
              titrant: formatSig(fmt, ct, sig + GUARD),
              result: formatSig(fmt, volume, sig + GUARD),
            })}
          </p>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copyVolume, { result: text })}
        />
      </>
    );
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="tit-v-analyte"
          label={strings.analyteConcentration}
          numeric
          onChange={setAnalyte}
          value={analyte}
        />
        <TextField
          id="tit-v-analyte-vol"
          label={strings.analyteVolume}
          numeric
          onChange={setAnalyteVol}
          value={analyteVol}
        />
        <TextField
          id="tit-v-titrant"
          label={strings.titrantConcentration}
          numeric
          onChange={setTitrant}
          value={titrant}
        />
      </div>
      <RatioFields onChange={setRatio} ratio={ratio} strings={strings} />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

export default function TitrationCalculatorWidget() {
  const strings = useToolUi(TITRATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("concentration");
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="tit-mode"
        onChange={setMode}
        options={[
          { value: "concentration", label: strings.modeConcentration },
          { value: "volume", label: strings.modeVolume },
          { value: "curve", label: strings.modeCurve },
        ]}
        value={mode}
      />
      {mode === "concentration" ? (
        <ConcentrationMode fmt={fmt} strings={strings} />
      ) : null}
      {mode === "volume" ? <VolumeMode fmt={fmt} strings={strings} /> : null}
      {mode === "curve" ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <TitrationCurvePanel fmt={fmt} strings={strings} />
        </section>
      ) : null}
    </>
  );
}
